import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';

import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Assembly, AssemblyCreate, AssemblyDetail, AssemblyFilter, AssemblyUpdate } from '@core/types/assembly.type';

@Injectable({ providedIn: 'root' })
export class AssemblyService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/assemblies`;

  grid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<Assembly>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    const filter = (config.filter || {}) as AssemblyFilter;
    if (filter.assemblyType !== undefined) params = params.set('filter.assemblyType', String(filter.assemblyType));
    if (filter.status !== undefined) params = params.set('filter.status', String(filter.status));
    if (filter.fromDate) params = params.set('filter.fromDate', filter.fromDate);
    if (filter.toDate) params = params.set('filter.toDate', filter.toDate);

    return this.http.get<ApiResponse<PaginationResult<Assembly>>>(`${this.baseUrl}/grid`, { params }).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading assemblies')),
    );
  }

  get(id: string): Observable<ApiResponse<AssemblyDetail>> {
    return this.http.get<ApiResponse<AssemblyDetail>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading assembly')),
    );
  }

  create(data: AssemblyCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error creating assembly')),
    );
  }

  update(data: AssemblyUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error updating assembly')),
    );
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error deleting assembly')),
    );
  }
}

